import axios from 'axios';
import api from '@/lib/axios';
import { uploadEntityDocument, listEntityDocuments } from '@/lib/documentsApi';
import { positivePoId } from './poAttachmentFlow';
import { validatePoFinancialContext, poFinancialWritesReady, verifyPoFinancialReadback } from './poFinancialForm';
import { notifyOperationalContextChanged } from './procurementOperationalContext';

// Financial writes must not pass through the generic API client's automatic retries.
const client = axios.create({
  baseURL: api.defaults.baseURL, withCredentials: true, timeout: 30000,
  headers: { Accept: 'application/json' },
});
async function beforeWrite(payload) {
  if (!payload?.expected_context) throw new Error('The PO company and unit could not be verified. Reopen the form.');
  try {
    const { data } = await client.get('/purchase-orders/form-financial-context');
    const context = validatePoFinancialContext(data, payload.expected_context);
    if (!poFinancialWritesReady(context)) throw new Error('The PO form update is awaiting server activation.');
    if (payload.expected_currencies && (context.unit_currency !== payload.expected_currencies.unit_currency || context.reporting_currency !== payload.expected_currencies.reporting_currency)) throw new Error('The unit or company currencies changed. Reopen the PO form before saving.');
    if (payload.fx_entry && context.current_rules.manual_fx_entry !== true) throw new Error('Exchange-rate entry is awaiting server activation.');
  } catch (error) {
    if (error.code === 'PO_CONTEXT_CHANGED') notifyOperationalContextChanged();
    throw error;
  }
}
async function write(request) {
  try { return (await request()).data; }
  catch (error) {
    if (error.response?.data?.code === 'PO_CONTEXT_CHANGED') notifyOperationalContextChanged();
    throw error;
  }
}
export const poDraftTransport = {
  async create(payload) { await beforeWrite(payload); return write(() => client.post('/purchase-orders/create', payload)); },
  async update(id, payload) { await beforeWrite(payload); return write(() => client.put(`/purchase-orders/${positivePoId(id)}/draft`, payload)); },
  async submit(id, payload) { await beforeWrite(payload); return write(() => client.post(`/purchase-orders/${positivePoId(id)}/submit`, {expected_context:payload.expected_context,...(payload.expected_fx_version ? {expected_fx_version:payload.expected_fx_version} : {})})); },
  async summary(id) { return (await client.get(`/purchase-orders/${positivePoId(id)}/summary`, { params: { _ts: Date.now() } })).data; },
  async verifyFinancials(id, payload, status) {
    try { return verifyPoFinancialReadback(await this.summary(id), payload, id, status); }
    catch (error) { error.reviewRequired = true; throw error; }
  },
  async list(id, signal) { return listEntityDocuments('purchase_order', positivePoId(id), signal); },
  async upload(id, entry, progress) {
    return uploadEntityDocument('purchase_order', positivePoId(id), entry.file, {
      document_type: entry.category, title: entry.title, description: entry.notes || '',
    }, event => { if (event.total) progress(Math.min(100, Math.round(event.loaded * 100 / event.total))); });
  },
};
export function poSaveError(error) {
  const original = error?.cause || error;
  const status = original?.response?.status;
  if (status === 401) return 'Your session is unavailable. Sign in again and reopen the saved draft.';
  if (status === 403) return 'The current account is not allowed to perform this PO/document action.';
  if (status === 409) return original.response.data?.error || 'The PO changed or is busy. Refresh its state before retrying.';
  if (status >= 400 && status < 500) return String(original.response?.data?.error || 'The request was rejected. Check the order and attachment details.').slice(0, 350);
  if (axios.isAxiosError(original)) return 'The server response was not confirmed. Check the saved state before another attempt.';
  return error?.message || 'The PO operation could not be completed.';
}
